import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import Modal from "@/components/admin/Modal";
import { Field, TextInput, TextArea } from "@/components/admin/FormControls";
import { FileText, BookOpen, Award, Pencil, Save, Plus, Trash2, RotateCcw, Loader2, ExternalLink } from "lucide-react";

const PAGES = [
    { slug: "sobre", label: "Sobre", icon: BookOpen, url: "/sobre" },
    { slug: "termos", label: "Termos de uso", icon: FileText, url: "/termos" },
    { slug: "privacidade", label: "Política de privacidade", icon: FileText, url: "/privacidade" },
    { slug: "certificados", label: "Certificados", icon: Award, url: "/certificados" },
];

// ============================================================
// EDITORS — one for each page shape
// ============================================================
function RepeatingList({ items, onChange, render, addLabel, testIdPrefix, empty }) {
    const update = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, empty]);
    return (
        <div className="space-y-3">
            {items.map((it, i) => (
                <div key={i} className="bg-[#FAFAF7] rounded-2xl p-5 border border-black/5 relative">
                    <button onClick={() => remove(i)} className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-red-50 text-red-600" data-testid={`${testIdPrefix}-remove-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {render(it, (v) => update(i, v), i)}
                </div>
            ))}
            <button type="button" onClick={add} data-testid={`${testIdPrefix}-add`} className="text-sm font-display text-[#5C5C5C] hover:text-[#1A1A1A] inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> {addLabel}
            </button>
        </div>
    );
}

function SobreEditor({ value, onChange }) {
    const c = value;
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
                <Field label="Eyebrow"><TextInput testId="sobre-eyebrow" value={c.eyebrow} onChange={(v) => onChange({ ...c, eyebrow: v })} /></Field>
                <Field label="Headline · parte normal"><TextInput testId="sobre-pre" value={c.headline_pre} onChange={(v) => onChange({ ...c, headline_pre: v })} /></Field>
            </div>
            <Field label="Headline · parte em itálico (destaque holográfico)"><TextInput testId="sobre-em" value={c.headline_em} onChange={(v) => onChange({ ...c, headline_em: v })} /></Field>
            <Field label="Texto de introdução"><TextArea testId="sobre-intro" rows={4} value={c.intro} onChange={(v) => onChange({ ...c, intro: v })} /></Field>

            <Field label="3 cards (Missão · Visão · Valores)">
                <RepeatingList items={c.cards || []} onChange={(v) => onChange({ ...c, cards: v })} testIdPrefix="sobre-card" addLabel="Adicionar card" empty={{ eyebrow: "", title: "", body: "" }} render={(it, upd, i) => (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <TextInput testId={`sobre-card-${i}-eyebrow`} value={it.eyebrow} onChange={(v) => upd({ ...it, eyebrow: v })} placeholder="Eyebrow" />
                            <TextInput testId={`sobre-card-${i}-title`} value={it.title} onChange={(v) => upd({ ...it, title: v })} placeholder="Título" />
                        </div>
                        <TextArea testId={`sobre-card-${i}-body`} rows={2} value={it.body} onChange={(v) => upd({ ...it, body: v })} placeholder="Descrição" />
                    </div>
                )} />
            </Field>

            <Field label="Métricas (números em destaque)">
                <RepeatingList items={c.stats || []} onChange={(v) => onChange({ ...c, stats: v })} testIdPrefix="sobre-stat" addLabel="Adicionar métrica" empty={{ n: "", l: "" }} render={(it, upd, i) => (
                    <div className="grid grid-cols-2 gap-3">
                        <TextInput testId={`sobre-stat-${i}-n`} value={it.n} onChange={(v) => upd({ ...it, n: v })} placeholder="Número (ex.: 2.000+)" />
                        <TextInput testId={`sobre-stat-${i}-l`} value={it.l} onChange={(v) => upd({ ...it, l: v })} placeholder="Legenda" />
                    </div>
                )} />
            </Field>

            <div className="border-t border-black/5 pt-6 space-y-4">
                <Field label="Bloco final · eyebrow"><TextInput testId="sobre-method-eyebrow" value={c.method_eyebrow} onChange={(v) => onChange({ ...c, method_eyebrow: v })} /></Field>
                <Field label="Bloco final · título"><TextInput testId="sobre-method-title" value={c.method_title} onChange={(v) => onChange({ ...c, method_title: v })} /></Field>
                <Field label="Bloco final · texto"><TextArea testId="sobre-method-body" rows={4} value={c.method_body} onChange={(v) => onChange({ ...c, method_body: v })} /></Field>
            </div>
        </div>
    );
}

function LegalEditor({ value, onChange, prefix }) {
    const c = value;
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-3 gap-4">
                <Field label="Eyebrow"><TextInput testId={`${prefix}-eyebrow`} value={c.eyebrow} onChange={(v) => onChange({ ...c, eyebrow: v })} /></Field>
                <Field label="Título"><TextInput testId={`${prefix}-title`} value={c.title} onChange={(v) => onChange({ ...c, title: v })} /></Field>
                <Field label="Última atualização" hint="ex.: 25/05/2026"><TextInput testId={`${prefix}-last`} value={c.last_updated || ""} onChange={(v) => onChange({ ...c, last_updated: v })} /></Field>
            </div>
            <Field label="Seções">
                <RepeatingList items={c.sections || []} onChange={(v) => onChange({ ...c, sections: v })} testIdPrefix={`${prefix}-section`} addLabel="Adicionar seção" empty={{ heading: "", paragraphs: [], list: [] }} render={(it, upd, i) => (
                    <div className="space-y-3">
                        <TextInput testId={`${prefix}-section-${i}-h`} value={it.heading} onChange={(v) => upd({ ...it, heading: v })} placeholder="Título da seção" />
                        <Field label="Parágrafos" hint="Um parágrafo por linha em branco entre eles">
                            <TextArea testId={`${prefix}-section-${i}-p`} rows={4} value={(it.paragraphs || []).join("\n\n")} onChange={(v) => upd({ ...it, paragraphs: v.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean) })} placeholder="Texto do(s) parágrafo(s)..." />
                        </Field>
                        <Field label="Lista (bullets)" hint="Uma linha = um item">
                            <TextArea testId={`${prefix}-section-${i}-l`} rows={3} value={(it.list || []).join("\n")} onChange={(v) => upd({ ...it, list: v.split("\n").map((s) => s.trim()).filter(Boolean) })} placeholder="Item 1&#10;Item 2&#10;Item 3" />
                        </Field>
                    </div>
                )} />
            </Field>
        </div>
    );
}

function CertificadosEditor({ value, onChange }) {
    const c = value;
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
                <Field label="Eyebrow"><TextInput testId="cert-eyebrow" value={c.eyebrow} onChange={(v) => onChange({ ...c, eyebrow: v })} /></Field>
                <Field label="Headline · parte normal"><TextInput testId="cert-pre" value={c.headline_pre} onChange={(v) => onChange({ ...c, headline_pre: v })} /></Field>
            </div>
            <Field label="Headline · parte em itálico"><TextInput testId="cert-em" value={c.headline_em} onChange={(v) => onChange({ ...c, headline_em: v })} /></Field>
            <Field label="Texto de introdução"><TextArea testId="cert-intro" rows={3} value={c.intro} onChange={(v) => onChange({ ...c, intro: v })} /></Field>

            <Field label="3 pilares (ícones fixos no design)">
                <RepeatingList items={c.pillars || []} onChange={(v) => onChange({ ...c, pillars: v })} testIdPrefix="cert-pillar" addLabel="Adicionar pilar" empty={{ title: "", body: "" }} render={(it, upd, i) => (
                    <div className="space-y-2">
                        <TextInput testId={`cert-pillar-${i}-title`} value={it.title} onChange={(v) => upd({ ...it, title: v })} placeholder="Título do pilar" />
                        <TextArea testId={`cert-pillar-${i}-body`} rows={2} value={it.body} onChange={(v) => upd({ ...it, body: v })} placeholder="Descrição do pilar" />
                    </div>
                )} />
            </Field>

            <div className="border-t border-black/5 pt-6 space-y-3">
                <Field label="CTA · título (aparece para visitantes não-logados)"><TextInput testId="cert-cta-eyebrow" value={c.cta_eyebrow} onChange={(v) => onChange({ ...c, cta_eyebrow: v })} /></Field>
                <Field label="CTA · texto"><TextArea testId="cert-cta-body" rows={2} value={c.cta_body} onChange={(v) => onChange({ ...c, cta_body: v })} /></Field>
            </div>
        </div>
    );
}

function EditorRouter({ slug, value, onChange }) {
    if (slug === "sobre") return <SobreEditor value={value} onChange={onChange} />;
    if (slug === "termos") return <LegalEditor value={value} onChange={onChange} prefix="termos" />;
    if (slug === "privacidade") return <LegalEditor value={value} onChange={onChange} prefix="privacy" />;
    if (slug === "certificados") return <CertificadosEditor value={value} onChange={onChange} />;
    return null;
}

// ============================================================
// PAGE
// ============================================================
export default function AdminPages() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // {slug, label, content}
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/admin/pages").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const open = (page) => {
        const remote = items.find((x) => x.slug === page.slug);
        setEditing({ ...page, content: remote?.content || {} });
        setError("");
    };

    const save = async () => {
        setError(""); setSaving(true);
        try {
            await api.put(`/admin/pages/${editing.slug}`, { content: editing.content });
            await load();
            setEditing(null);
        } catch (e) { setError(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const resetDefaults = async () => {
        if (!window.confirm("Restaurar conteúdo padrão? As alterações serão perdidas.")) return;
        try {
            const { data } = await api.post(`/admin/pages/${editing.slug}/reset`);
            setEditing({ ...editing, content: data.content });
            await load();
        } catch (e) { alert(formatApiError(e)); }
    };

    return (
        <AdminLayout eyebrow="Conteúdo" title="Páginas do site">
            <p className="font-body text-[#5C5C5C] mb-8">Edite o conteúdo das páginas institucionais. As alterações aparecem em tempo real no site.</p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {PAGES.map((p) => (
                    <article key={p.slug} data-testid={`page-card-${p.slug}`} className="bg-white rounded-3xl p-7 border border-black/5">
                        <div className="w-10 h-10 rounded-xl bg-[#F8F8F5] flex items-center justify-center">
                            <p.icon className="w-5 h-5 text-[#1A1A1A]" />
                        </div>
                        <h3 className="font-display text-xl tracking-tight mt-5">{p.label}</h3>
                        <div className="font-body text-xs text-[#5C5C5C] mt-1">/{p.slug}</div>
                        <div className="flex gap-2 mt-6">
                            <button onClick={() => open(p)} data-testid={`page-edit-${p.slug}`} className="btn-primary text-sm flex-1 justify-center">
                                <Pencil className="w-3.5 h-3.5" /> Editar
                            </button>
                            <a href={p.url} target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid={`page-preview-${p.slug}`}>
                                <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </div>
                    </article>
                ))}
            </div>

            <Modal open={!!editing} onClose={() => setEditing(null)} title={editing ? `Editar · ${editing.label}` : ""} size="xl">
                {editing && (
                    <>
                        <EditorRouter slug={editing.slug} value={editing.content} onChange={(content) => setEditing({ ...editing, content })} />
                        {error && <div data-testid="page-error" className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{error}</div>}
                        <div className="flex items-center justify-between gap-3 pt-6 mt-6 border-t border-black/5">
                            <button onClick={resetDefaults} className="text-sm font-display text-[#5C5C5C] hover:text-red-600 inline-flex items-center gap-1.5" data-testid="page-reset">
                                <RotateCcw className="w-3.5 h-3.5" /> Restaurar padrão
                            </button>
                            <div className="flex gap-3">
                                <button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button>
                                <button onClick={save} data-testid="page-save" className="btn-primary" disabled={saving}>
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </Modal>
        </AdminLayout>
    );
}
